import React from 'react';
import {
  useTranslations,
  type TranslationFunction,
} from '@unisporkal/localization';
import {
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import { ButtonGroupSelector } from '../../../molecules/ButtonGroupSelector';
import { type SizeOption } from '../../../../types/sizeOption';
import styles from './ALCBuyCard.module.scss';
import assetSizeLabel from '../../../../support/assetSizeLabel';

type SizeOptionsSelectorProps = {
  sizeOptions: SizeOption[];
  selectedOption: SizeOption;
  onSelectSize: (name: string) => void;
};

const toPixelSizes = (t: TranslationFunction, size: SizeOption) =>
  size.pixelWidth && size.pixelHeight
    ? t('img_size_in_pixels', {
        width: size.pixelWidth,
        height: size.pixelHeight,
      })
    : '';

const toUnitSizes = (t: TranslationFunction, size: SizeOption) =>
  size.localeWidth && size.localeHeight && size.unit
    ? t('img_size_in_units', {
        width: size.localeWidth,
        height: size.localeHeight,
        units: t(size.unit),
      })
    : '';

const toDimensionLine = (t: TranslationFunction, size: SizeOption) => {
  const pixelSizes = toPixelSizes(t, size);
  const unitSizes = toUnitSizes(t, size);
  return pixelSizes && unitSizes ? `${pixelSizes} (${unitSizes})` : pixelSizes;
};

const toResolutionLine = (t: TranslationFunction, size: SizeOption) => {
  const megapixelSizes = size.megapixels
    ? t('img_size_in_megapixels', { size: size.megapixels })
    : '';
  return size.dpi && megapixelSizes
    ? `${size.dpi} dpi | ${megapixelSizes}`
    : '';
};

const toSizeDetails = (t: TranslationFunction, size: SizeOption) =>
  [toDimensionLine(t, size), toResolutionLine(t, size)]
    .filter(Boolean)
    .join(' ');

const SizeOptionsSelector = ({
  sizeOptions,
  selectedOption,
  onSelectSize,
}: SizeOptionsSelectorProps) => {
  const t = useTranslations();
  const sizeDetails = toSizeDetails(t, selectedOption);

  return (
    <div className={styles.sizeSelector}>
      <Typography
        variant={TypographyVariant.body.small}
        component={TypographyComponentEnum.DIV}
      >
        {t('size_options')}
      </Typography>
      <ButtonGroupSelector
        options={sizeOptions.map((option) => ({
          value: option.name,
          label: assetSizeLabel(t, option),
          testId: `alc-size-option-${option.name}`,
        }))}
        value={selectedOption.name}
        onChange={onSelectSize}
        ariaLabel={t('size_options')}
      />
      {sizeDetails && (
        <Typography
          variant={TypographyVariant.body.xsmall}
          component={TypographyComponentEnum.DIV}
          className={styles.sizeDetails}
          data-testid="alc-buy-card-size-details"
        >
          {sizeDetails}
        </Typography>
      )}
    </div>
  );
};

export default SizeOptionsSelector;
